import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { API } from "../lib/api";
import axios from "axios";
import { Anvil, MapPin, Star, ShieldCheck, ArrowRight, ArrowLeft } from "lucide-react";

const initials = (n) => (n || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
const cap = (s) => (s || "").charAt(0).toUpperCase() + (s || "").slice(1);

function Stars({ value }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`w-4 h-4 ${i <= Math.round(value) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}
    </div>
  );
}

function setJsonLd(obj) {
  let el = document.getElementById("city-jsonld");
  if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = "city-jsonld"; document.head.appendChild(el); }
  el.textContent = JSON.stringify(obj);
}

export default function CityDirectory() {
  const { city } = useParams();
  const cityName = cap(decodeURIComponent(city || ""));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = `Selliers-garnisseurs à ${cityName} | Fabriko`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
    meta.content = `Trouvez les meilleurs selliers-garnisseurs à ${cityName} : sellerie auto, marine, bâcherie, coussins. Avis clients vérifiés.`;
    setLoading(true);
    axios.get(`${API}/public/directory`, { params: { city: cityName } })
      .then((r) => {
        setItems(r.data);
        setJsonLd({
          "@context": "https://schema.org", "@type": "ItemList", name: `Selliers-garnisseurs à ${cityName}`,
          itemListElement: r.data.map((a, i) => ({ "@type": "ListItem", position: i + 1,
            item: { "@type": "LocalBusiness", name: a.name, address: { "@type": "PostalAddress", addressLocality: a.city } } })),
        });
      })
      .catch(() => setItems([])).finally(() => setLoading(false));
  }, [cityName]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-navy text-white">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/annuaire" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"><ArrowLeft className="w-4 h-4" /> Annuaire</Link>
          <div className="flex items-center gap-2"><div className="w-8 h-8 rounded-lg bg-sky-400 flex items-center justify-center"><Anvil className="w-4 h-4 text-navy" /></div><span className="font-display font-bold">Fabriko</span></div>
        </div>
      </header>

      <section className="bg-navy-deep text-white">
        <div className="max-w-6xl mx-auto px-5 py-12">
          <h1 className="text-3xl sm:text-4xl font-display font-bold">Selliers-garnisseurs à {cityName}</h1>
          <p className="text-slate-300 mt-2 max-w-2xl">{items.length} artisan{items.length > 1 ? "s" : ""} près de {cityName} — sellerie auto & marine, bâcherie, coussins. Avis clients vérifiés.</p>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-5 py-10">
        {loading ? <p className="text-center text-slate-400 py-16">Chargement…</p>
          : items.length === 0 ? (
            <div className="text-center py-16" data-testid="city-empty">
              <p className="text-slate-400">Aucun artisan référencé à {cityName} pour le moment.</p>
              <Link to="/annuaire" className="text-sky-600 font-semibold hover:underline mt-2 inline-block">Voir tout l'annuaire →</Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="city-grid">
              {items.map((a) => (
                <Link key={a.id} to={`/annuaire/${a.id}`} data-testid={`city-card-${a.id}`}
                  className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md hover:border-sky-300 transition-all flex flex-col">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-xl bg-navy text-sky-300 flex items-center justify-center font-display font-bold shrink-0">{initials(a.name)}</div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-display font-semibold text-navy leading-tight truncate">{a.name}</h3>
                        {a.member_sts && <span className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 text-[10px] font-bold"><ShieldCheck className="w-3 h-3" /> STS</span>}
                      </div>
                      {a.city && <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {a.city}</div>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3"><Stars value={a.rating} /><span className="text-sm font-semibold text-navy">{a.rating || "—"}</span><span className="text-xs text-slate-400">({a.reviews_count} avis)</span></div>
                  {a.specialties?.length > 0 && <div className="flex flex-wrap gap-1.5 mt-3">{a.specialties.slice(0, 3).map((s) => <span key={s} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px]">{s}</span>)}</div>}
                  <span className="mt-4 inline-flex items-center gap-1 text-sm text-sky-600 font-semibold">Voir la fiche <ArrowRight className="w-4 h-4" /></span>
                </Link>
              ))}
            </div>
          )}
      </main>
      <footer className="bg-navy text-slate-300 mt-10"><div className="max-w-6xl mx-auto px-5 py-8 text-center text-sm">Annuaire Fabriko · Selliers-garnisseurs à {cityName}</div></footer>
    </div>
  );
}
